import { useState } from 'react';
import { useTranslate } from '@refinedev/core';
import { PushpinFilled } from '@ant-design/icons';
import {
  Button,
  Card,
  Grid,
  Segmented,
  Table,
  Tag,
  Timeline,
  Typography,
  theme,
} from 'antd';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import type {
  PublicationAudioOut,
  PublicationDetailOut,
  PublicationEbookOut,
} from '@/api/generated/schemas';
import { ModStatusTag } from '@/components/common/ModStatusTag';
import { EarlyAccessCard } from '@/components/user_catalog/publications/EarlyAccessCard';
import { formatDate } from '@/utils/format';
import { availabilityStatusColors } from '../constants';
import { orDash } from '@/components/common/show/shared';
import type {
  RangeState,
  TimelineEvent,
  TimelineSource,
} from './publicationTimeline';
import {
  dateRangeState,
  publicationEvents,
  todayMarkerIndex,
} from './publicationTimeline';
import { ExclusivityModal } from './ExclusivityModal';
import { exclusivityState, rangeToPickerValue } from './exclusivityWindow';
import { exclusiveUntilToPickerValue } from './formatExclusiveUntil';

const { Text } = Typography;

interface PublicationAvailabilityTabProps {
  record: PublicationDetailOut;
}

interface WindowRow {
  key: string;
  lower?: string | null;
  upper?: string | null;
}

interface ExclusivityRow {
  key: string;
  lower: Dayjs | null;
  upper: Dayjs | null;
  state: RangeState;
}

interface FormatRow {
  key: string;
  format: PublicationEbookOut | PublicationAudioOut;
  label: string;
}

const rangeStateColors: Record<RangeState, string | undefined> = {
  active: 'green',
  ended: undefined,
  planned: 'blue',
};

export const PublicationAvailabilityTab = ({
  record,
}: PublicationAvailabilityTabProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();
  const [timelineFilter, setTimelineFilter] = useState<TimelineSource>('pub');
  // One modal covers the window and the per-format dates, so both cards open it.
  const [exclusivityModalOpen, setExclusivityModalOpen] = useState(false);
  const now = dayjs();

  const T = 'user_catalog.publications.show';

  const windowRows: WindowRow[] = (record.available_ranges ?? []).map(
    (range, index) => ({
      key: `availability-${index}`,
      lower: range.lower,
      upper: range.upper,
    })
  );

  // Displayed as inclusive days so the table and the modal agree.
  const exclusiveDays = rangeToPickerValue(record.exclusive_ranges);
  const windowState = exclusivityState(record.exclusive_ranges, now);
  const exclusivityRows: ExclusivityRow[] =
    exclusiveDays && windowState
      ? [
          {
            key: 'exclusivity',
            lower: exclusiveDays[0],
            upper: exclusiveDays[1],
            state: windowState,
          },
        ]
      : [];

  const formatRows: FormatRow[] = [
    ...(record.ebook
      ? [
          {
            key: 'ebook',
            format: record.ebook,
            label: t('user_catalog.publications.tabs.ebook'),
          },
        ]
      : []),
    ...(record.audio
      ? [
          {
            key: 'audio',
            format: record.audio,
            label: t('user_catalog.publications.tabs.audio'),
          },
        ]
      : []),
  ];

  const events = publicationEvents(record).filter(
    (event) => event.src === timelineFilter
  );
  const todayIndex = todayMarkerIndex(events, now);
  // Set by hand, not derived — stays vivid whether past or upcoming.
  const highlightItem = (event: TimelineEvent) => ({
    dot: <PushpinFilled style={{ color: token.purple }} />,
    children: (
      <>
        <Tag color="purple">{t(event.labelKey, event.labelParams)}</Tag>
        <div>
          <Text strong style={{ fontSize: token.fontSizeSM }}>
            {formatDate(event.date)}
          </Text>
        </div>
      </>
    ),
  });
  const timelineItems = [
    ...events.slice(0, todayIndex).map((event) =>
      event.highlight
        ? highlightItem(event)
        : {
            color: event.end ? 'gray' : 'blue',
            children: (
              <>
                <div>{t(event.labelKey, event.labelParams)}</div>
                <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                  {formatDate(event.date)}
                </Text>
              </>
            ),
          }
    ),
    {
      color: token.colorSuccess,
      children: (
        <>
          <div style={{ color: token.colorSuccessText }}>
            {t(`${T}.timeline.today`)}
          </div>
          <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
            {now.format('DD.MM.YYYY')}
          </Text>
        </>
      ),
    },
    ...events.slice(todayIndex).map((event) =>
      event.highlight
        ? highlightItem(event)
        : {
            color: 'gray',
            children: (
              <>
                <div style={{ color: token.colorTextTertiary }}>
                  {t(event.labelKey, event.labelParams)}
                </div>
                <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                  {formatDate(event.date)}
                </Text>
              </>
            ),
          }
    ),
  ];

  const statusCell = (label: string, children: React.ReactNode) => (
    <div>
      <div
        style={{
          fontSize: token.fontSizeSM,
          color: token.colorTextTertiary,
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );

  return (
    <div style={{ display: 'grid', gap: token.margin }}>
      <Card>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: screens.md ? 'repeat(3, 1fr)' : '1fr',
            gap: token.marginLG,
          }}
        >
          {statusCell(
            t('user_catalog.publications.fields.availability_status'),
            record.availability_status ? (
              <Tag
                color={
                  availabilityStatusColors[record.availability_status] ??
                  'default'
                }
              >
                {t(
                  `user_catalog.publications.availability_status.${record.availability_status}`
                )}
              </Tag>
            ) : (
              orDash(null)
            )
          )}
          {statusCell(
            t('base.fields.mod_status'),
            <ModStatusTag value={record.mod_status} />
          )}
          {statusCell(
            t('user_catalog.publications.fields.switch_mode'),
            record.switch_mode
              ? t(`user_catalog.publications.switch_mode.${record.switch_mode}`)
              : orDash(null)
          )}
        </div>
      </Card>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: screens.lg ? '1fr 380px' : '1fr',
          gap: token.margin,
          alignItems: 'start',
        }}
      >
        <div style={{ display: 'grid', gap: token.margin }}>
          <Card
            title={t(`${T}.sections.exclusivity`)}
            extra={
              <Button
                type="link"
                size="small"
                onClick={() => setExclusivityModalOpen(true)}
              >
                {t(
                  record.exclusive_ranges
                    ? `${T}.exclusivity.edit`
                    : `${T}.exclusivity.set`
                )}
              </Button>
            }
          >
            <Table<ExclusivityRow>
              dataSource={exclusivityRows}
              rowKey="key"
              pagination={false}
              size="small"
              locale={{ emptyText: t(`${T}.exclusivity.empty`) }}
              columns={[
                {
                  title: t('common.range.from'),
                  key: 'lower',
                  render: (_, row) => orDash(row.lower?.format('DD.MM.YYYY')),
                },
                {
                  title: t('common.range.to'),
                  key: 'upper',
                  render: (_, row) =>
                    row.upper ? (
                      row.upper.format('DD.MM.YYYY')
                    ) : (
                      <Text type="secondary">
                        {t(`${T}.windows.open_ended`)}
                      </Text>
                    ),
                },
                {
                  title: t(`${T}.windows.state`),
                  key: 'state',
                  render: (_, row) => (
                    <Tag color={rangeStateColors[row.state]}>
                      {t(`${T}.windows.state_${row.state}`)}
                    </Tag>
                  ),
                },
              ]}
            />
          </Card>

          <EarlyAccessCard record={record} />

          <Card title={t(`${T}.sections.publication_windows`)}>
            <Table<WindowRow>
              dataSource={windowRows}
              rowKey="key"
              pagination={false}
              size="small"
              locale={{ emptyText: orDash(null) }}
              columns={[
                {
                  title: t('common.range.from'),
                  dataIndex: 'lower',
                  render: (value: string | null | undefined) =>
                    orDash(formatDate(value)),
                },
                {
                  title: t('common.range.to'),
                  dataIndex: 'upper',
                  render: (value: string | null | undefined) =>
                    value ? (
                      formatDate(value)
                    ) : (
                      <Text type="secondary">
                        {t(`${T}.windows.open_ended`)}
                      </Text>
                    ),
                },
                {
                  title: t(`${T}.windows.state`),
                  key: 'state',
                  render: (_, row) => {
                    const state = dateRangeState(
                      { lower: row.lower, upper: row.upper },
                      now
                    );
                    return (
                      <Tag color={rangeStateColors[state]}>
                        {t(`${T}.windows.state_${state}`)}
                      </Tag>
                    );
                  },
                },
              ]}
            />
          </Card>

          <Card
            title={t(`${T}.sections.format_windows`)}
            extra={
              formatRows.length > 0 && (
                <Button
                  type="link"
                  size="small"
                  onClick={() => setExclusivityModalOpen(true)}
                >
                  {t(
                    formatRows.some((row) => row.format.exclusive_until)
                      ? `${T}.exclusivity.edit`
                      : `${T}.exclusivity.set`
                  )}
                </Button>
              )
            }
          >
            <Table<FormatRow>
              dataSource={formatRows}
              rowKey="key"
              pagination={false}
              size="small"
              locale={{ emptyText: orDash(null) }}
              columns={[
                {
                  title: t(`${T}.windows.format`),
                  dataIndex: 'label',
                },
                {
                  title: t(`${T}.windows.published_at`),
                  key: 'published_at',
                  render: (_, row) =>
                    orDash(formatDate(row.format.published_at)),
                },
                {
                  title: t(`${T}.windows.first_available`),
                  key: 'first_available_from',
                  render: (_, row) =>
                    orDash(formatDate(row.format.first_available_from)),
                },
                {
                  title: t(`${T}.windows.available_from`),
                  key: 'available_from',
                  render: (_, row) =>
                    orDash(formatDate(row.format.available_from)),
                },
                {
                  title: t(`${T}.windows.available_till`),
                  key: 'available_till',
                  render: (_, row) =>
                    orDash(formatDate(row.format.available_till)),
                },
                {
                  title: t(`${T}.windows.exclusive_until`),
                  key: 'exclusive_until',
                  // The last exclusive day — the stored value is the first day after it.
                  render: (_, row) =>
                    orDash(
                      exclusiveUntilToPickerValue(
                        row.format.exclusive_until
                      )?.format('DD.MM.YYYY')
                    ),
                },
                {
                  title: t(`${T}.windows.withdrawal`),
                  key: 'withdrawal_at',
                  render: (_, row) =>
                    orDash(formatDate(row.format.withdrawal_at)),
                },
              ]}
            />
          </Card>
        </div>

        <Card
          title={t(`${T}.sections.timeline`)}
          extra={
            <Segmented
              value={timelineFilter}
              onChange={(value) => setTimelineFilter(value as TimelineSource)}
              options={[
                { label: t(`${T}.timeline.filter_pub`), value: 'pub' },
                { label: t(`${T}.timeline.filter_ebook`), value: 'ebook' },
                { label: t(`${T}.timeline.filter_audio`), value: 'audio' },
              ]}
            />
          }
        >
          <Timeline items={timelineItems} />
        </Card>
      </div>

      <ExclusivityModal
        record={record}
        open={exclusivityModalOpen}
        onClose={() => setExclusivityModalOpen(false)}
      />
    </div>
  );
};
